import DocumentationDialogHandler from "ips/ac/controller/scfld/ObjectDisplay.controller";
import ErrorService from "ips/ac/util/ErrorService";
import CONSTANTS from "ips/ac/util/Constants";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import SmarterTable from "ips/ac/syscon/SmarterTable";
import MessageToast from "sap/m/MessageToast";
import formatter from "ips/ac/model/formatter";
import ODataMixin from "ips/ac/controller/scfld/mixin/ODataMixin";
import OrderLabelsMethods from "ips/ac/controller/scfld/mixin/OrderLabelsMethods";
import JSONModel from "sap/ui/model/json/JSONModel";
import ODataCRUDUtil from "ips/ac/util/ODataCRUDUtil";
import ViewHandler from "ips/ac/base/handler/ViewHandler";
import LinkOrderDialogController from "ips/ac/data/order/controller/LinkOrderDialog.controller";
import AssignToOrderDialogController, { AssignToOrderType } from "ips/ac/data/order/controller/AssignToOrderDialog.controller";
import Table from "sap/m/Table";
import Dialog, { Dialog$AfterCloseEvent } from "sap/m/Dialog";
import { Select$ChangeEvent } from "sap/m/Select";
import { SearchField$SearchEvent } from "sap/m/SearchField";
import { Button$PressEvent } from "sap/m/Button";
import { SelectDialog$SearchEvent } from "sap/m/SelectDialog";
import ListItemBase from "sap/m/ListItemBase";
import ObjectPageLayout from "sap/uxap/ObjectPageLayout";
import XMLView from "sap/ui/core/mvc/XMLView";
import UI5Element from "sap/ui/core/Element";
import Event from "sap/ui/base/Event";
import Router from "sap/ui/core/routing/Router";
import { Route$MatchedEvent, Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import ListBinding from "sap/ui/model/ListBinding";
import Context from "sap/ui/model/odata/v4/Context";
import ODataModel from "sap/ui/model/odata/v4/ODataModel";
import ODataListBinding, { ODataListBinding$DataReceivedEvent } from "sap/ui/model/odata/v4/ODataListBinding";

type FilterValue = Filter | Filter[];
type CalendarUserConfig = Record<string, number | boolean | string>;

interface ObjectPageLayoutWithScroll extends ObjectPageLayout {
  _scrollTo(iY: number, iTime: number): void;
}

/**
 * @namespace ips.ac.data.order.controller
 */
export default class Order extends DocumentationDialogHandler {

  public formatter = formatter;
  private _documentationDialogFragmentName = "ips.ac.data.order.fragment.DocumentationFormDialog";
  private _oCustomCalendarViewConfigModel: JSONModel;

  public onInit(): void {
    super.onInit();
    this._oViewModel.setProperty("/LinkedDetailsCount", undefined);
    this._oViewModel.setProperty("/documentationDialogFragmentName", this._documentationDialogFragmentName);
    new SmarterTable(this, "OrderLinkedEntityDetail", "", []);
    ODataMixin.addTo(this);
    OrderLabelsMethods.addTo(this);

    this.getRouter().getRoute(this._sRouteName).attachMatched(this._scrollUpRouteMatched, this);

  }

  protected _afterViewBound(): void {
    this._setShowGeneratePDF();
  }

  private _setShowGeneratePDF(): void {
    this._oViewModel.setProperty("/showGeneratePDF", false);
    const oFilter = new Filter("Category_Value", FilterOperator.EQ, "DOCUMENTATIONS");
    this._oODataModel.bindList("FileDetails", this._getViewContext(), undefined, oFilter).requestContexts()
      .then((arContexts) => {
        const sOrderStatus = this._getViewContext().getProperty("Status_Value") as string;
        if (sOrderStatus === "CLOSED" && arContexts.length === 0) {
          this._oViewModel.setProperty("/showGeneratePDF", true);
        }
      });
  }

  protected _onObjectMatched(oEvent: Route$PatternMatchedEvent): void {
    const oParameter = oEvent.getParameter("arguments") as { path?: string },
      sOrderPath = oParameter ? oParameter.path : null,
      aOrderMatch: string[] = sOrderPath ? sOrderPath.match(/\('([^ ]*)'\)/) : [],
      sOrderGuid = aOrderMatch.length > 1 ? aOrderMatch[1] : "";
    super._onObjectMatched(oEvent);
    this._bindPrincipalTable(sOrderGuid);


    this._oViewModel.setProperty("/tableButtonVisible", true);

    void this._loadCount(sOrderPath + "/OrderLineDetails", "/OrderlineCount");
    void this._loadAttachmentCount(sOrderPath);
  }

  private _bindPrincipalTable(sOrderGuid: string): void {

    const oPrincipalTable = this.getView().byId("orderPrincipalDetailTable") as Table;
    if (!oPrincipalTable) {
      return;
    }
    const oBinding = this._filterTableItems(oPrincipalTable, [
      new Filter("InstanceGuid", FilterOperator.EQ, sOrderGuid),
      new Filter("ContactType_Value", FilterOperator.EQ, "PRINCIPAL"),
    ]);
    // attached in code, because the fragment is also used by OrderCopyDeep
    oBinding.detachDataReceived(this._handleOrderDataReceived, this);
    oBinding.attachDataReceived(this._handleOrderDataReceived, this);
  }

  /**
   * Filters the (initially suspended) items binding defined in the XML view and resumes it on first use.
   */
  private _filterTableItems(oTable: Table, vFilter: FilterValue): ODataListBinding {
    const oBinding = oTable.getBinding("items") as ODataListBinding;
    oBinding.filter(vFilter);
    if (oBinding.isSuspended()) {
      oBinding.resume();
    }
    return oBinding;
  }


  public onContactAdd(oEvent: Event<{ listItem?: UI5Element; item?: UI5Element }, UI5Element>): void {
    const oItem = oEvent.getParameter("listItem") || oEvent.getParameter("item") || oEvent.getSource();
    oItem.data("entityDefaults", {
      "ContactType_Id": "CONTACT_CONTACTTYPE",
      "ContactType_Value": "PRINCIPAL",
      "InstanceType_Id": "INSTANCE_TYPE",
      "InstanceType_Value": "ORDER",
    });
    this.onAdd(oEvent);
  }

  public onDeleteAddress(oEvent: Button$PressEvent): void {
    const sPath = oEvent.getSource().getBindingContext().getPath(),
      sQuestion = this.getText("deleteText");
    this.confirmDeletionByUser({
      question: sQuestion,
    }, [sPath], () => {
      this._showAddButton("addressAddButtonVisible");
    }, function() {
    });
  }

  public onEditAddress(oEvent: Button$PressEvent): void {
    const sPath = oEvent.getSource().getBindingContext().getPath(),
      sFromTarget = this._sRouteName,
      sEditRoute = "addressedit",
      sFromLayout = this._oSettings.layout;
    let sLayout: string = this.getComponent().getRouteLayout(sEditRoute);

    if (!sLayout) {
      sLayout = this.getComponent().getHelper().getNextUIState(2).layout;
    }

    const bUseRouting = CONSTANTS.GENERAL.USE_ROUTING as boolean,
      oConfig = {
        layout: sLayout,
        mode: "update",
        //path: encodeURIComponent(sPath.substr(1)),
        path: sPath.substr(1),
        fromTarget: sFromTarget,
        fromLayout: sFromLayout,
      };

    if (bUseRouting) {
      //this._oStorage.put(sEditRoute, oConfig);
      this.setRouteDisplayData(sEditRoute, oConfig);
      this.navTo(sEditRoute, oConfig);
    } else {
      this.display(sEditRoute, oConfig);
      this.getComponent().setLayout(sLayout);
    }
  }

  private _handleOrderDataReceived(oEvent: ODataListBinding$DataReceivedEvent): void {
    const sPath = oEvent.getSource().getPath();
    const sModelProperty = (sPath === "AddressDetails") ? "addressAddButtonVisible" : "principalAddButtonVisible";
    const iCountResults = parseInt((oEvent.getParameter("data") as { __count?: string }).__count, 10);
    if (iCountResults && iCountResults > 0) {
      this._hideAddButton(sModelProperty);
    } else {
      this._showAddButton(sModelProperty);
    }
  }

  private _hideAddButton(sModelProperty: string): void {
    (this.getView().getModel("ui") as JSONModel).setProperty("/" + sModelProperty, false);

  }

  private _showAddButton(sModelProperty: string): void {
    (this.getView().getModel("ui") as JSONModel).setProperty("/" + sModelProperty, true);
  }

  public onChangeStatusButton(oEvent: Event): void {
    // refreshing the order context afterwards triggers _afterViewBound, which updates the PDF button
    this.onChangeListButton(oEvent);
  }

  public async onOpenLinkOrderDialog(): Promise<void> {
    const oView = await ViewHandler.getView("ips.ac.data.order.view.LinkOrderDialog", this.getComponent());
    const oContext = this._getViewContext();
    await (oView.getController() as LinkOrderDialogController).openDialog({
      orderGuid: oContext.getProperty("Guid") as string,
      projectGuid: oContext.getProperty("ProjectGuid") as string,
      orderPath: oContext.getPath(),
    }, () => {
      // only the linked orders table is reloaded, its binding uses $$ownRequest
      (this.getView().byId("OrderLinkedEntityDetail") as Table).getBinding("items").refresh();
    });
  }

  public async onChangeLinkTypeInTable(oEvent: Select$ChangeEvent): Promise<void> {

    try {
      const oContext = oEvent.getSource().getBindingContext() as Context;
      await ODataCRUDUtil.submitBatch();
      oContext.refresh();


    } catch (err) {
      console.error(err);
    }
  }

  public onSelect(oEvent: Event<{ selectedContexts?: Context[] }>): void {
    const aSelectedContexts = oEvent.getParameter("selectedContexts");
  }

  public onSearchAddDialog(oEvent: SelectDialog$SearchEvent): void {
    const sValue = oEvent.getParameter("value"),
      arStringFilterValues = ["Title", "Description", "Guid"],
      oBinding = oEvent.getSource().getBinding("items") as ListBinding;
    let arFilter: Filter[] = [];
    if (sValue !== "") {
      arFilter = this._getFiltersFromProperties(arStringFilterValues, sValue);
      oBinding.filter([new Filter({
        filters: [
          // this._getDefaultFilter(),
          new Filter(arFilter, false),
        ],
        and: true,
      })]);
    } else {
      oBinding.filter(arFilter);
    }
  }

  public onSearchPlanning(oEvent: SearchField$SearchEvent): void {
    const oTable = this.findParentControl<Table>("sap.m.Table", oEvent.getSource());
    const oFilter = this._getFilterFromSF(oEvent.getSource());
    (oTable.getBinding("items") as ListBinding).filter(oFilter);


  }

  public onRemoveOrderEntity(oEvent: Button$PressEvent): void {
    const oItem = oEvent.getSource().getParent() as ListItemBase,
      oTable = oItem.getParent() as Table,
      sOrderEntityContext = oItem.getBindingContext() as Context;
    oTable.setBusy(true);
    this.deleteOneEntity(sOrderEntityContext, () => {
        MessageToast.show(this.getText("@ValueHelpSuccessfullyDeleted"));
        // only the affected table (teams or resources) is reloaded, see _openAssignDialog
        oTable.getBinding("items").refresh();
        oTable.setBusy(false);
      },
      () => {
        oTable.setBusy(false);
        ErrorService.checkODataError.call(this);
      });
  }

  //
  // Assign teams / resources
  //

  public async onOpenAssignTeamsDialog(): Promise<void> {
    await this._openAssignDialog("team");
  }

  public async onOpenAssignResourcesDialog(): Promise<void> {
    await this._openAssignDialog("resource");
  }

  private async _openAssignDialog(sType: AssignToOrderType): Promise<void> {
    const oContext = this._getViewContext();
    // only the affected table is reloaded, both bindings use $$ownRequest so they can be refreshed on their own
    const sTableId = sType === "team" ? "orderOrderTeamsTable" : "orderOrderResourcesTable";
    await AssignToOrderDialogController.open(sType, {
      orderGuid: oContext.getProperty("Guid") as string,
      orderPath: oContext.getPath(),
    }, () => {
      (this.getView().byId(sTableId) as Table).getBinding("items").refresh();
    });
  }

  private _scrollUpRouteMatched(oEvent: Route$MatchedEvent): void {
    (this.getView().byId("ObjectPageLayout") as ObjectPageLayoutWithScroll)._scrollTo(0, 0);
  }

  private _getOrderGuidForInputSubmit(): string {
    const oOrderContext = this._getViewContext();
    return oOrderContext.getProperty("Guid") as string;
  }

  private _getViewContext(): Context {
    return this.getView().getBindingContext() as Context;
  }


  //
  // AddAppointmentTable
  //
  public onCreateAppointment(oEvent: Button$PressEvent): void {
    this._setCalendarUserConfig();
    void this.openAppointmentDialog(this.newAppointmentEntry());
  }

  public navToAppointmentDialog(vAppointmentProperty: string | Context): Router {
    const sPath = typeof (vAppointmentProperty) === "string" ? vAppointmentProperty : vAppointmentProperty.getPath();
    return this.getRouter().navTo("taskCalendarAppointment", { path: sPath.replace("/", "") });
  }

  public newAppointmentEntry(oProperties?: object): object {
    if (!oProperties) {
      const oDefaultAppointmentStatus = this.getComponent().getUser().getUserConfigurationById("DEFAULT_APPOINTMENT_STATUS");
      const oDefaultAppointmentType = this.getComponent().getUser().getUserConfigurationById("DEFAULT_APPOINTMENT_TYPE");
      oProperties = {
        "Start": new Date(),
        "End": new Date(new Date().getTime() + 15 * 60 * 1000),
        "Status_Value": oDefaultAppointmentStatus?.Value || "PLANNED",
        "Type_Value": oDefaultAppointmentType?.Value || "INSTALLATION",
        "OrderDetail": this._getViewContext().getObject(),
      };
    }
    return oProperties;
  }

  //TO-DO: Outsource into a functions.js file with CalendarBase Function
  private _getCalendarUserConfig(): CalendarUserConfig {
    const oDefaultStatus = this.getComponent().getUser().getUserConfigurationById("DEFAULT_APPOINTMENT_STATUS");
    const oDefaultType = this.getComponent().getUser().getUserConfigurationById("DEFAULT_APPOINTMENT_TYPE");
    const oUserConfig: CalendarUserConfig = {
      "DAY_START": 6,
      "DAY_END": 18,
      "FIRST_WEEK_DAY": 1,
      "LAST_WORK_DAY": 6,
      "SHOW_ALL_DAYS": 1,
    };

    const arUserConfig = this.getComponent().getUser().getUserConfiguration();
    arUserConfig.forEach((oConfig) => {
      if (!oConfig) {
        return;
      }
      if (oConfig.ConfigurationId) {
        const sConfigId = oConfig.ConfigurationId;
        if (oConfig.ConfigurationId in oUserConfig) {
          oUserConfig[sConfigId] = parseInt(oConfig.Value.substring(0, 2));
        }
      }
    });
    // todo: refactor
    oUserConfig["SHOW_ALL_DAYS"] = this._intToBool(oUserConfig["SHOW_ALL_DAYS"] as number);
    if (oDefaultStatus) oUserConfig["DEFAULT_APPOINTMENT_STATUS"] = oDefaultStatus.Value;
    if (oDefaultType) oUserConfig["DEFAULT_APPOINTMENT_TYPE"] = oDefaultType.Value;


    return oUserConfig;
  }

  private _setCalendarUserConfig(): void {
    this._oCustomCalendarViewConfigModel = new JSONModel(this._getCalendarUserConfig());
    this.setModel(this._oCustomCalendarViewConfigModel, "customCalendarViewConfig");
  }

  private _intToBool(iValue: number): boolean {
    return iValue === 1;
  }

  public onChangeListButton(oEvent: Event, fnSuccess?: () => void): void {
    super.onChangeListButton(oEvent, () => {
      _fnRefreshOrderLineDetails.call(this);
      fnSuccess?.();
    });
  }

  public onCloseAppointmentDialog(oEvent: Dialog$AfterCloseEvent): void {
    this.onClosePopup(oEvent);
    this.getModel<ODataModel>().resetChanges();
    // "updated" is set on the dialog by the appointment dialog logic
    if ((oEvent.getSource() as Dialog & { updated?: boolean }).updated) {
      this._getViewContext().refresh();
      this.getComponent().refreshCalendar = {
        taskCalendar: true,
        teamCalendar: true,
      };
    }
  }

  public loadAppointmentConfig(): Promise<unknown[]> {
    return Promise.resolve([]);
  }

  public onUpdateInternalStatus(): void {
    this._getViewContext().refresh();
  }
}

function _fnRefreshOrderLineDetails(this: Order): void {
  const oSubView = this.getView().byId("orderLineDetails") as XMLView;
  let oList: UI5Element;
  (this.getView().getBindingContext() as Context).refresh();
  if (!oSubView) {
    return;
  }
  oList = oSubView.getController().getView().byId("List");
  if (oList && oList.getBinding("items")) {
    (oList.getBinding("items").getContext() as Context).refresh();
  }
}
